import { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import * as Sentry from "@sentry/react";
import { ThemeProvider } from './contexts/ThemeContext';

import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Vehicles from './pages/Vehicles';
import Receipts from './pages/Receipts';
import Schedule from './pages/Schedule';
import Chat from './pages/Chat';
import Deliveries from './pages/Deliveries';
import Layout from './components/Layout';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import VehicleManagement from './pages/admin/VehicleManagement';
import StaffManagement from './pages/admin/StaffManagement';
import ScheduleEditor from './pages/admin/ScheduleEditor';
import Analytics from './pages/admin/Analytics';
import ReceiptsManagement from './pages/admin/ReceiptsManagement';
import MaintenanceCalendar from './pages/admin/MaintenanceCalendar';
import LiveTracking from './pages/admin/LiveTracking';
import GeofenceManagement from './pages/admin/GeofenceManagement';

// Super Admin pages
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import TenantsManagement from './pages/super-admin/TenantsManagement';
import TenantsList from './pages/super-admin/TenantsList';
import TenantDetail from './pages/super-admin/TenantDetail';
import SuperAdminOverview from './pages/super-admin/SuperAdminOverview';
import WebhookMonitor from './pages/super-admin/WebhookMonitor';
import UsageHistory from './pages/super-admin/UsageHistory';

// Onboarding pages
import OnboardingCompany from './pages/onboarding/OnboardingCompany';
import OnboardingDrivers from './pages/onboarding/OnboardingDrivers';
import OnboardingCashX from './pages/onboarding/OnboardingCashX';
import OnboardingQRTest from './pages/onboarding/OnboardingQRTest';
import OnboardingDone from './pages/onboarding/OnboardingDone';

import TenantRegister from './pages/TenantRegister';
import BillingDashboard from './pages/admin/BillingDashboard';
import BillingSuccess from './pages/BillingSuccess';
import PayrollManagement from './pages/admin/PayrollManagement';
import MyPayroll from './pages/MyPayroll';
import VacationRequest from './pages/VacationRequest';
import VacationManagement from './pages/admin/VacationManagement';
import TenantSettings from './pages/admin/TenantSettings';
import TimeTracking from './pages/TimeTracking';
import TimeTrackingAdmin from './pages/admin/TimeTrackingAdmin';
import LexwareSync from './pages/admin/LexwareSync';
import BackupManagement from './pages/admin/BackupManagement';
import ShiftPreferences from './pages/ShiftPreferences';
import ShiftPreferencesAdmin from './pages/admin/ShiftPreferencesAdmin';
import ReportDamage from './pages/ReportDamage';
import WebhookAdmin from './pages/admin/WebhookAdmin';
import LandingPage from './pages/LandingPage';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import AGB from './pages/AGB';
import CookieBanner from './components/CookieBanner';
import PaymentDemo from './pages/PaymentDemo';

// Legal Pages (new DSGVO-compliant)
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import CookiesPolicy from './pages/CookiesPolicy';
import DPA from './pages/DPA';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.baseURL = API;
axios.defaults.withCredentials = true;

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const justAuth = sessionStorage.getItem('just_authenticated');
      if (!justAuth) {
        await new Promise(r => setTimeout(r, 150));
      } else {
        sessionStorage.removeItem('just_authenticated');
      }

      // First check for Cash-X driver token
      const cashxToken = localStorage.getItem('cashx_driver_token');
      const driverId = localStorage.getItem('driver_id');
      const driverName = localStorage.getItem('driver_name');
      const driverEmail = localStorage.getItem('driver_email');
      const tenantId = localStorage.getItem('tenant_id');

      if (cashxToken && driverId) {
        // Cash-X authenticated driver
        setUser({
          id: driverId,
          name: driverName || 'Fahrer',
          email: driverEmail,
          role: 'driver',
          tenant_id: tenantId
        });
        setIsAuthenticated(true);
        return;
      }

      // Fallback to Flotto backend auth check
      try {
        const res = await axios.get('/auth/me');
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
        navigate('/', { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    return <Navigate to="/" replace />;
  }

  return <Layout user={user}>{children}</Layout>;
}

function AppRouter() {
  return (
    <Routes>
      {/* Public Routes - Landing Page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />
      
      {/* New DSGVO-compliant Legal Pages */}
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/cookies" element={<CookiesPolicy />} />
      <Route path="/legal/dpa" element={<DPA />} />
      <Route path="/legal/imprint" element={<Impressum />} />
      
      {/* Payment Demo (Development Only) */}
      <Route path="/payment-demo" element={<ProtectedRoute><PaymentDemo /></ProtectedRoute>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/tenant/register" element={<TenantRegister />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/vehicles" element={<ProtectedRoute><Vehicles /></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><Receipts /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/deliveries" element={<ProtectedRoute><Deliveries /></ProtectedRoute>} />
      <Route path="/report-damage" element={<ProtectedRoute><ReportDamage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/vehicles" element={<ProtectedRoute><VehicleManagement /></ProtectedRoute>} />
      <Route path="/admin/staff" element={<ProtectedRoute><StaffManagement /></ProtectedRoute>} />
      <Route path="/admin/schedules" element={<ProtectedRoute><ScheduleEditor /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/admin/receipts" element={<ProtectedRoute><ReceiptsManagement /></ProtectedRoute>} />
      <Route path="/admin/maintenance" element={<ProtectedRoute><MaintenanceCalendar /></ProtectedRoute>} />
      <Route path="/admin/tracking" element={<ProtectedRoute><LiveTracking /></ProtectedRoute>} />
      <Route path="/admin/geofencing" element={<ProtectedRoute><GeofenceManagement /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute><BillingDashboard /></ProtectedRoute>} />
      <Route path="/admin/payroll" element={<ProtectedRoute><PayrollManagement /></ProtectedRoute>} />
      <Route path="/admin/vacation" element={<ProtectedRoute><VacationManagement /></ProtectedRoute>} />
      <Route path="/admin/time-tracking" element={<ProtectedRoute><TimeTrackingAdmin /></ProtectedRoute>} />
      <Route path="/admin/lexware" element={<ProtectedRoute><LexwareSync /></ProtectedRoute>} />
      <Route path="/admin/backups" element={<ProtectedRoute><BackupManagement /></ProtectedRoute>} />
      <Route path="/admin/shift-preferences" element={<ProtectedRoute><ShiftPreferencesAdmin /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><TenantSettings /></ProtectedRoute>} />
      <Route path="/admin/webhooks" element={<ProtectedRoute><WebhookAdmin /></ProtectedRoute>} />
      
      {/* Driver/Employee Routes */}
      <Route path="/my-payroll" element={<ProtectedRoute><MyPayroll /></ProtectedRoute>} />
      <Route path="/vacation" element={<ProtectedRoute><VacationRequest /></ProtectedRoute>} />
      <Route path="/time-tracking" element={<ProtectedRoute><TimeTracking /></ProtectedRoute>} />
      <Route path="/shift-preferences" element={<ProtectedRoute><ShiftPreferences /></ProtectedRoute>} />
      
      {/* Billing Routes */}
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/billing/cancel" element={<ProtectedRoute><BillingDashboard /></ProtectedRoute>} />
      
      {/* Super Admin Routes */}
      <Route path="/super-admin" element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/super-admin/overview" element={<ProtectedRoute><SuperAdminOverview /></ProtectedRoute>} />
      <Route path="/super-admin/tenants" element={<ProtectedRoute><TenantsList /></ProtectedRoute>} />
      <Route path="/super-admin/tenants/:tenantId" element={<ProtectedRoute><TenantDetail /></ProtectedRoute>} />
      <Route path="/super-admin/tenants/:tenantId/usage-history" element={<ProtectedRoute><UsageHistory /></ProtectedRoute>} />
      <Route path="/super-admin/webhooks" element={<ProtectedRoute><WebhookMonitor /></ProtectedRoute>} />
      
      {/* Onboarding Routes */}
      <Route path="/onboarding/company" element={<ProtectedRoute><OnboardingCompany /></ProtectedRoute>} />
      <Route path="/onboarding/drivers" element={<ProtectedRoute><OnboardingDrivers /></ProtectedRoute>} />
      <Route path="/onboarding/cashx" element={<ProtectedRoute><OnboardingCashX /></ProtectedRoute>} />
      <Route path="/onboarding/qr-test" element={<ProtectedRoute><OnboardingQRTest /></ProtectedRoute>} />
      <Route path="/onboarding/done" element={<ProtectedRoute><OnboardingDone /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
          <BrowserRouter>
            <AppRouter />
            <CookieBanner />
          </BrowserRouter>
          <Toaster position="top-right" />
        </Sentry.ErrorBoundary>
      </div>
    </ThemeProvider>
  );
}

// Error Fallback Component
function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Etwas ist schiefgelaufen</h2>
        <p className="text-gray-600 mb-6">
          Ein unerwarteter Fehler ist aufgetreten. Unser Team wurde automatisch benachrichtigt.
        </p>
        <button
          onClick={() => window.location.href = '/'}
          className="w-full bg-gradient-to-r from-red-500 to-orange-600 text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-200"
        >
          Zur Startseite
        </button>
      </div>
    </div>
  );
}

export default App;
